import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const TIERS = ["silver", "gold", "platinum", "premium", "custom"];

export default function AdminPackages() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/packages", { params: { include_inactive: true } }),
        api.get("/categories", { params: { include_unpublished: true } }),
      ]);
      setItems(p.data);
      setCats(c.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload, id) => {
    try {
      if (id) await api.patch(`/admin/packages/${id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Saved");
      setEditing(null); setCreating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"?`)) return;
    try { await api.delete(`/admin/packages/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-end justify-between">
        <div><span className="kbd-eyebrow">Admin</span><h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Packages</h1></div>
        <button data-testid="admin-pkg-add-button" onClick={() => setCreating(true)} className="btn-primary"><Plus className="h-4 w-4" /> Add package</button>
      </div>

      <div className="mt-8 space-y-3" data-testid="admin-packages-list">
        {loading && <div className="rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border/70 bg-card p-10 text-center text-sm text-muted-foreground" data-testid="admin-pkg-empty">No packages yet.</div>
        )}
        {items.map((p) => (
          <PackageRow key={p.id} p={p} cats={cats} onEdit={() => setEditing(p)} onDelete={() => del(p)} />
        ))}
      </div>

      {(editing || creating) && (
        <PackageModal initial={editing} cats={cats}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={(payload) => save(payload, editing?.id)} />
      )}
    </div>
  );
}

function PackageRow({ p, cats, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const catById = Object.fromEntries(cats.map((c) => [c.id, c]));
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-5" data-testid={`admin-pkg-row-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">{p.tier}</div>
          <div className="mt-0.5 font-serif-display text-xl font-semibold text-secondary">{p.name}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Price: {p.price !== null && p.price !== undefined ? `₹${Number(p.price).toLocaleString("en-IN")}${p.pricing_unit ? ` / ${p.pricing_unit}` : ""}` : <span className="text-primary font-semibold">TO BE CONFIGURED</span>}
            {" · "}Min: {p.minimum_guests ?? <span className="text-primary font-semibold">TO BE CONFIGURED</span>}
            {" · "}Rules: {p.category_rules?.length || 0}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`admin-pkg-toggle-${p.id}`} onClick={() => setOpen((v) => !v)} className="grid h-8 w-8 place-items-center rounded-full border border-border/70 hover:bg-muted">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button data-testid={`admin-pkg-edit-${p.id}`} onClick={onEdit} className="grid h-8 w-8 place-items-center rounded-full border border-border/70 hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`admin-pkg-delete-${p.id}`} onClick={onDelete} className="grid h-8 w-8 place-items-center rounded-full border border-border/70 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {open && (
        <div className="mt-4 border-t border-border/60 pt-4" data-testid={`admin-pkg-expand-${p.id}`}>
          {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
          <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Category selection rules</div>
          {(!p.category_rules || p.category_rules.length === 0) ? (
            <p className="mt-1 text-xs text-primary font-semibold">TO BE CONFIGURED — no category rules set yet.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {p.category_rules.map((r, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg border border-border/70 bg-background px-3 py-2">
                  <span>{catById[r.category_id]?.name || r.category_id}</span>
                  <span className="text-xs text-muted-foreground">Select {r.select_count === null || r.select_count === undefined ? <span className="text-primary font-semibold">TO BE CONFIGURED</span> : `${r.select_count} items`} · {r.required ? "required" : "optional"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function PackageModal({ initial, cats, onClose, onSave }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    tier: initial?.tier || "silver",
    description: initial?.description || "",
    price: initial?.price ?? "",
    pricing_unit: initial?.pricing_unit || "",
    minimum_guests: initial?.minimum_guests ?? "",
    active: initial?.active ?? true,
    display_order: initial?.display_order ?? 0,
    category_rules: initial?.category_rules || [],
  });
  const s = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const addRule = () => setF((p) => ({ ...p, category_rules: [...p.category_rules, { category_id: "", select_count: null, required: true }] }));
  const updateRule = (i, patch) => setF((p) => ({ ...p, category_rules: p.category_rules.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  const removeRule = (i) => setF((p) => ({ ...p, category_rules: p.category_rules.filter((_, idx) => idx !== i) }));

  const menuCats = cats.filter((c) => !c.is_extra && c.active !== false);

  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name required");
    onSave({
      name: f.name.trim(), tier: f.tier, description: f.description || null,
      price: f.price === "" ? null : Number(f.price),
      pricing_unit: f.pricing_unit || null,
      minimum_guests: f.minimum_guests === "" ? null : Number(f.minimum_guests),
      active: f.active, display_order: Number(f.display_order) || 0,
      category_rules: f.category_rules
        .filter((r) => r.category_id)
        .map((r) => ({
          category_id: r.category_id,
          select_count: r.select_count === null || r.select_count === "" ? null : Number(r.select_count),
          required: !!r.required,
        })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="admin-pkg-modal">
      <form onSubmit={submit} className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border/70 bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl font-semibold text-secondary">{initial ? "Edit package" : "New package"}</h3>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <F label="Name" className="sm:col-span-2"><input data-testid="admin-pkg-name-input" required value={f.name} onChange={(e) => s("name", e.target.value)} className="pk-input" /></F>
          <F label="Tier"><select data-testid="admin-pkg-tier-select" value={f.tier} onChange={(e) => s("tier", e.target.value)} className="pk-input">{TIERS.map((t) => (<option key={t} value={t}>{t}</option>))}</select></F>
          <F label="Price (₹) — empty = TO BE CONFIGURED"><input data-testid="admin-pkg-price-input" type="number" min="0" step="0.01" value={f.price} onChange={(e) => s("price", e.target.value)} className="pk-input" /></F>
          <F label="Pricing unit"><input data-testid="admin-pkg-unit-input" value={f.pricing_unit} onChange={(e) => s("pricing_unit", e.target.value)} className="pk-input" placeholder="per person" /></F>
          <F label="Minimum guests"><input data-testid="admin-pkg-min-guests-input" type="number" min="1" value={f.minimum_guests} onChange={(e) => s("minimum_guests", e.target.value)} className="pk-input" /></F>
          <F label="Description" className="sm:col-span-2"><textarea data-testid="admin-pkg-desc-input" rows={2} value={f.description} onChange={(e) => s("description", e.target.value)} className="pk-input" /></F>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="admin-pkg-active-toggle" checked={f.active} onChange={(e) => s("active", e.target.checked)} /> Active</label>
          <F label="Display order"><input data-testid="admin-pkg-order-input" type="number" value={f.display_order} onChange={(e) => s("display_order", e.target.value)} className="pk-input" /></F>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Category selection rules</div>
              <button type="button" data-testid="admin-pkg-add-rule" onClick={addRule} className="inline-flex items-center gap-1 rounded-full border border-border/70 px-3 py-1 text-xs font-semibold text-secondary hover:bg-muted"><Plus className="h-3.5 w-3.5" /> Add rule</button>
            </div>
            {f.category_rules.length === 0 && <p className="mt-2 text-xs text-primary font-semibold">TO BE CONFIGURED — no rules added.</p>}
            <ul className="mt-2 space-y-2" data-testid="admin-pkg-rules-list">
              {f.category_rules.map((r, i) => (
                <li key={i} className="grid grid-cols-[1fr_120px_100px_auto] items-center gap-2 rounded-xl border border-border/70 bg-background p-2">
                  <select data-testid={`admin-pkg-rule-cat-${i}`} value={r.category_id} onChange={(e) => updateRule(i, { category_id: e.target.value })} className="pk-input !py-1.5">
                    <option value="">Select category…</option>
                    {menuCats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                  <input data-testid={`admin-pkg-rule-count-${i}`} type="number" min="0" placeholder="Select X" value={r.select_count ?? ""} onChange={(e) => updateRule(i, { select_count: e.target.value === "" ? null : Number(e.target.value) })} className="pk-input !py-1.5" />
                  <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" data-testid={`admin-pkg-rule-required-${i}`} checked={r.required} onChange={(e) => updateRule(i, { required: e.target.checked })} /> req.</label>
                  <button type="button" data-testid={`admin-pkg-rule-remove-${i}`} onClick={() => removeRule(i)} className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="admin-pkg-save-button" className="btn-primary">Save</button>
        </div>
      </form>
      <style>{`.pk-input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.65rem 1rem; font-size:0.875rem; }
      .pk-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function F({ label, className = "", children }) {
  return (<label className={`block text-sm ${className}`}><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>{children}</label>);
}
